import { Directive, forwardRef, HostBinding, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslationService } from 'app/core/internationalization/translation.service';
import { KH_A11Y_TARGET } from '../constants/a11y-target.token';
import { A11yRole } from '../interfaces/a11y.interfaces';
import { normalizeAriaValue, normalizeToken } from '../utils/a11y.util';

import { A11yLabelDirective } from './a11y-label.directive';

const A11Y_REGION_ROLES = ['region', 'search', 'navigation', 'group'] as const;

@Directive({
  selector: '[khA11yRegion]',
  providers: [{ provide: KH_A11Y_TARGET, useExisting: forwardRef(() => A11yRegionDirective) }]
})
export class A11yRegionDirective extends A11yLabelDirective implements A11yRole {
  private readonly _translationService = inject(TranslationService);
  private _defaultRoleDescription = 'Region';

  @Input() public khA11yRegionRole: 'region' | 'search' | 'navigation' | 'group' = 'region';
  @Input() public khA11yRegionRoleDescription: string | null = null;

  public constructor() {
    super();
    this._translationService.translationsReady$
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this._defaultRoleDescription = this._translationService.instant('A11Y_REGION.REGION') || this._defaultRoleDescription;
      });
  }

  @HostBinding('attr.role')
  public get role(): 'region' | 'search' | 'navigation' | 'group' {
    return normalizeToken(this.khA11yRegionRole, A11Y_REGION_ROLES, 'region') ?? 'region';
  }

  @HostBinding('attr.aria-roledescription')
  public get ariaRoleDescription(): string {
    return normalizeAriaValue(this.khA11yRegionRoleDescription) ?? this._defaultRoleDescription;
  }
}
